import { requestExactLoaderCleanup, type LoaderCleanupStatus } from "./editorLoaderCleanup";
import {
  sendToUnityWithResponse,
  type UnitySendOptions,
} from "../../components/Unityboards/UnityBridge";
import type { ViewRDataDelta } from "../../store/viewRDataStore";
import { getEditorVdbSaveApiConfig } from "./saveApi";

import { buildAndStageManifestPlan } from "./saveManifestPlanRuntime";

export const EDITOR_BRIDGE_EVENTS = {
  REQUEST_VIEWR_DATA: "reactToUnity_requestViewRData",
  SAVE_JSON: "reactToUnity_saveJson",
  UNLOAD_VIEWR: "reactToUnity_unloadViewR",
  COMMAND: "reactToUnity_command",
  APPLY_VIEWR_DELTA: "reactToUnity_applyViewRDelta",
  SET_INPUT_PROFILE: "reactToUnity_setInputProfile",
  RESET_VIEWPOINT: "reactToUnity_resetViewpoint",
  ACTIVATE_PROTOTYPE_MUTATION_UNDO:
    "reactToUnity_activatePrototypeMutationUndo",
  LOADER_VALIDATION_OUTBOX_STORED:
    "reactToUnity_loaderValidationOutboxStored",
  CLEANUP_LOADER_ATTEMPT: "reactToUnity_cleanupLoaderAttempt",
} as const;

export type UnitySender = (
  command: string,
  payload?: Record<string, unknown>,
  options?: UnitySendOptions,
) => Promise<void>;

export interface EditorBridgePort {
  requestViewRData: (
    payload?: Record<string, unknown>,
    options?: UnitySendOptions,
  ) => Promise<void>;
  requestSaveSnapshot: (
    payload?: Record<string, unknown>,
    options?: UnitySendOptions,
  ) => Promise<void>;
  unloadRuntime: (
    payload?: Record<string, unknown>,
    options?: UnitySendOptions,
  ) => Promise<void>;
  setInputProfile: (
    profile: "desktop" | "mobile",
    options?: UnitySendOptions,
  ) => Promise<void>;
  resetViewpoint: (options?: UnitySendOptions) => Promise<void>;
  activatePrototypeMutationUndo: (
    undoToken: string,
    options?: UnitySendOptions,
  ) => Promise<void>;
  ackLoaderValidationOutboxStored: (
    loaderJobId: string,
    generation: number,
    options?: UnitySendOptions,
  ) => Promise<void>;
  cleanupLoaderAttempt: (
    loaderJobId: string,
    generation: number,
    options?: UnitySendOptions,
  ) => Promise<LoaderCleanupStatus>;
  selectSpace: (
    payload: {
      targetSpaceIndex?: number;
      spaceIndex?: number;
      transactionId?: string | null;
      reason?: string;
      [key: string]: unknown;
    },
    options?: UnitySendOptions,
  ) => Promise<void>;
  applyViewRDelta: (
    delta: Pick<
      ViewRDataDelta,
      "mutationId" | "baseRevision" | "nextRevision" | "ops" | "source"
    >,
    options?: UnitySendOptions,
  ) => Promise<void>;
}

export function createEditorBridgePort(
  sendToUnity: UnitySender = sendToUnityWithResponse,
): EditorBridgePort {
  return {
    requestViewRData(payload = {}, options = {}) {
      return sendToUnity(EDITOR_BRIDGE_EVENTS.REQUEST_VIEWR_DATA, payload, options);
    },

    async requestSaveSnapshot(payload = {}, options = {}) {
      // VDB SHA-256 is available only after Unity packing, so a generic URL
      // prefetched before packing cannot be checksum-bound or cache-aware.
      const apiConfig = await getEditorVdbSaveApiConfig({
        allowPrefetchedUpload: false,
      });
      // Manifest save 판정은 저장 시작 지점이 여러 곳이므로 여기 한 곳에서만 한다.
      // null이면 기존 legacy full save이고 payload도 예전과 동일하다.
      const partialSpaceIndices = buildAndStageManifestPlan(
        typeof payload.saveAttemptId === "string" ? payload.saveAttemptId : "",
      );
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.SAVE_JSON,
        {
          ...payload,
          apiBaseUrl: apiConfig.apiBaseUrl,
          bearerToken: apiConfig.bearerToken,
          ...(partialSpaceIndices ? { partialSpaceIndices } : {}),
        },
        {
          ...options,
          requiresReadyState: "handlers_ready",
        },
      );
    },

    unloadRuntime(payload = {}, options = {}) {
      return sendToUnity(EDITOR_BRIDGE_EVENTS.UNLOAD_VIEWR, payload, options);
    },

    setInputProfile(profile, options = {}) {
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.SET_INPUT_PROFILE,
        { profile },
        options,
      );
    },

    resetViewpoint(options = {}) {
      return sendToUnity(EDITOR_BRIDGE_EVENTS.RESET_VIEWPOINT, {}, options);
    },

    activatePrototypeMutationUndo(undoToken, options = {}) {
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.ACTIVATE_PROTOTYPE_MUTATION_UNDO,
        { undoToken },
        options,
      );
    },

    ackLoaderValidationOutboxStored(loaderJobId, generation, options = {}) {
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.LOADER_VALIDATION_OUTBOX_STORED,
        { loader_job_id: loaderJobId, generation },
        options,
      );
    },

    cleanupLoaderAttempt(loaderJobId, generation, options = {}) {
      return requestExactLoaderCleanup(loaderJobId, generation, () => sendToUnity(
        EDITOR_BRIDGE_EVENTS.CLEANUP_LOADER_ATTEMPT,
        { loader_job_id: loaderJobId, generation },
        { ...options, dedupeKey: `loader-cleanup:${loaderJobId}:${generation}` },
      ), options.assertCurrent);
    },

    selectSpace(payload, options = {}) {
      const { targetSpaceIndex, spaceIndex, ...rest } = payload;
      const resolvedSpaceIndex =
        typeof spaceIndex === "number" ? spaceIndex : targetSpaceIndex;
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.COMMAND,
        {
          ...rest,
          command: "tool_selectSpace",
          ...(typeof resolvedSpaceIndex === "number"
            ? { spaceIndex: resolvedSpaceIndex }
            : {}),
        },
        options,
      );
    },

    applyViewRDelta(delta, options = {}) {
      return sendToUnity(
        EDITOR_BRIDGE_EVENTS.APPLY_VIEWR_DELTA,
        {
          mutationId: delta.mutationId,
          baseRevision: delta.baseRevision,
          nextRevision: delta.nextRevision,
          ops: delta.ops,
          source: delta.source,
        },
        options,
      );
    },
  };
}

export const editorBridgePort = createEditorBridgePort();
